/* ============================================================
   Corporate structure archetypes.

   A deliberate limit first, because it shapes everything below.

   This module contains no tax rates, no withholding percentages and no
   treaty outcomes. Not because they do not matter — they are often the
   whole reason a structure is chosen — but because they change with
   each finance act, they turn on facts about a specific taxpayer, and a
   number quoted confidently in a tool is a number someone will act on.
   The rest of this product states figures with a source and a date
   attached. There is no honest way to do that for a treaty position
   here, so it is not attempted.

   What IS stable is the structural question: how many legal entities,
   in what relationship, and what each arrangement costs to run. That is
   knowable, it changes slowly, and it is the part founders most often
   get wrong — usually by adopting a structure they read about, built
   for a problem they do not have, and then paying for it every year.

   So the output is an archetype, the reasoning that selected it, the
   ongoing cost of carrying it, and a list of questions generated from
   the specific answers given — questions to put to counsel, who can
   price the rates this module will not.
   ============================================================ */

export const QUESTIONS = [
  {
    id: 'footprint',
    label: 'Where does the operation actually trade?',
    hint: 'Where invoices are raised and staff sit — not where you hope to be.',
    options: [
      { value: 'single', label: 'One country', note: 'All revenue and staff in a single market.' },
      { value: 'few', label: 'Two or three markets', note: 'Typically a corridor: origin, transit, destination.' },
      { value: 'many', label: 'Four or more, or beyond the EAC', note: 'A regional business in substance, not just ambition.' }
    ]
  },
  {
    id: 'capital',
    label: 'Who is putting money in?',
    hint: 'Present investors and the ones you are actively raising from.',
    options: [
      { value: 'founders', label: 'Founders only', note: 'Self-funded or family money.' },
      { value: 'local', label: 'Local co-investors', note: 'Angels or partners resident in the same market.' },
      { value: 'foreign', label: 'Foreign private investors', note: 'Individuals or companies outside the operating country.' },
      { value: 'institutional', label: 'DFI or institutional fund', note: 'Development finance, private equity, or a venture fund.' }
    ]
  },
  {
    id: 'assets',
    label: 'What does the business own?',
    hint: 'Ownership, not use. Leased assets belong to whoever owns them.',
    options: [
      { value: 'light', label: 'Very little', note: 'Forwarding, agency or brokerage — people and systems.' },
      { value: 'fleet', label: 'Vehicles or equipment', note: 'Trucks, trailers, handling equipment, reefers.' },
      { value: 'property', label: 'Land or buildings', note: 'A yard, a warehouse, a depot — owned or on a long lease.' }
    ]
  },
  {
    id: 'exit',
    label: 'What is meant to happen in five to seven years?',
    hint: 'The honest answer, including "no idea".',
    options: [
      { value: 'hold', label: 'Keep running it', note: 'No sale contemplated.' },
      { value: 'raise', label: 'Raise a larger round', note: 'Institutional capital at a later stage.' },
      { value: 'sell', label: 'Sell to a trade buyer', note: 'A larger operator or a logistics group.' },
      { value: 'partner', label: 'Bring in a partner per market', note: 'Local shareholders in individual countries.' }
    ]
  },
  {
    id: 'intercompany',
    label: 'Would entities charge each other?',
    hint: 'Management fees, shared systems, one entity clearing for another.',
    options: [
      { value: 'no', label: 'No, or not yet', note: 'Each entity stands alone commercially.' },
      { value: 'yes', label: 'Yes', note: 'Services, licences or cost recharges cross a border.' }
    ]
  }
];

export const ARCHETYPES = {
  single: {
    id: 'single',
    label: 'Single operating company',
    shape: ['Operating company'],
    summary: 'One entity does everything. The cheapest structure to run, and the right answer more often than founders expect.',
    solves:
      'Nothing beyond trading, which is the point. Every additional entity is a set of accounts, a filing calendar and a director’s duty that someone has to carry.',
    carries: [
      'One set of statutory accounts and one annual return.',
      'No intercompany agreements, no transfer pricing file, no consolidation.'
    ],
    breaks: [
      'A trade buyer purchasing the company buys everything in it, including the liabilities — which is why sale processes on a single entity take longer and price lower.',
      'Bringing in an investor means diluting the same entity that holds the operating risk.'
    ]
  },
  holdco: {
    id: 'holdco',
    label: 'Holding company over one operating company',
    shape: ['Holding company', 'Operating company'],
    summary: 'Ownership sits one level above trading. Shares change hands at the top without disturbing licences, contracts or bank mandates below.',
    solves:
      'The transaction problem. Investors subscribe at holdco, so the operating entity’s licences, customer contracts and change-of-control clauses are untouched by a share issue.',
    carries: [
      'Two sets of accounts, and consolidated accounts once the group meets the threshold.',
      'A holdco with no employees and no premises still needs directors who actually direct it.'
    ],
    breaks: [
      'A holdco that exists only on paper invites the question of where it is really managed — and the answer determines where it is taxed.',
      'Dividends have to travel from opco to holdco before they reach shareholders, which is a step, not a formality.'
    ]
  },
  regional: {
    id: 'regional',
    label: 'Regional holding company over per-country operating companies',
    shape: ['Regional holding company', 'Opco — market A', 'Opco — market B', '…'],
    summary: 'One owner, several licensed operators. The standard shape for a corridor business that must be locally licensed in each market it touches.',
    solves:
      'Local licensing and local shareholding rules, which are usually not waivable. It also lets one market be sold, partnered or closed without unpicking the others.',
    carries: [
      'Accounts, audit and a filing calendar in every jurisdiction.',
      'Intercompany agreements between entities, and transfer pricing documentation once they charge each other.',
      'Genuine decision-making at the holding company, evidenced — board minutes that record decisions actually taken there.'
    ],
    breaks: [
      'Substance is the failure point. A holding company chosen for its treaty network, without people or decisions in it, is the arrangement tax authorities across the region are most actively challenging.',
      'Per-country minority shareholders can block a group-level sale unless the shareholders’ agreements anticipate it.'
    ]
  },
  spv: {
    id: 'spv',
    label: 'Holding company with asset-owning subsidiaries',
    shape: ['Holding company', 'Operating company', 'Asset SPV — fleet or property'],
    summary: 'Trading risk and asset ownership are separated, so an operational claim cannot reach the yard or the fleet.',
    solves:
      'Two things at once. Assets are ring-fenced from operational liability, and they can be financed on their own security — which is what asset financiers and property lenders prefer to lend against.',
    carries: [
      'A lease or hire agreement between the SPV and the opco, on arm’s-length terms, actually documented.',
      'An additional entity per financed asset class, with its own accounts and its own lender covenants.'
    ],
    breaks: [
      'An intercompany lease at a rate nobody can justify is the first thing an auditor and a revenue authority will both look at.',
      'Ring-fencing fails if the opco and SPV are run as one purse. The separation has to be real in the bank accounts, not only on the chart.'
    ]
  }
};

/**
 * Select an archetype from the answers.
 *
 * Ordered from most to least demanding, because the drivers stack: a
 * business that needs per-country entities also usually wants a holdco
 * above them, and saying so once is clearer than describing a hybrid.
 * Each branch records WHY, so the recommendation can be defended rather
 * than merely displayed.
 */
export function selectArchetype(a) {
  const reasons = [];

  const multiMarket = a.footprint === 'few' || a.footprint === 'many';
  const perMarketPartners = a.exit === 'partner';
  const heavyAssets = a.assets === 'fleet' || a.assets === 'property';
  const outsideCapital = a.capital === 'foreign' || a.capital === 'institutional';

  if (multiMarket || perMarketPartners) {
    if (multiMarket) {
      reasons.push(
        a.footprint === 'many'
          ? 'The operation trades in four or more markets, and freight licensing is granted nationally — each market needs an entity that can hold its own licence.'
          : 'The operation trades in more than one market, and freight licensing is granted nationally rather than regionally.'
      );
    }
    if (perMarketPartners) {
      reasons.push(
        'Bringing in a partner for a single market is only clean if that market is already its own entity. Retrofitting it later means transferring a licensed business.'
      );
    }
    if (heavyAssets) {
      reasons.push(
        'With owned assets in the picture, an asset-holding subsidiary sits naturally under the same regional holding company — see the note below on financing them separately.'
      );
    }
    return { archetype: ARCHETYPES.regional, reasons, alsoConsider: heavyAssets ? ARCHETYPES.spv : null };
  }

  if (heavyAssets && (outsideCapital || a.exit === 'sell' || a.exit === 'raise')) {
    reasons.push(
      a.assets === 'property'
        ? 'The business owns land or buildings, which is the asset class most worth separating — property outlives the operating business and is financed on its own terms.'
        : 'The business owns vehicles or equipment, which asset financiers prefer to lend against directly rather than through a trading entity.'
    );
    reasons.push(
      outsideCapital
        ? 'Outside capital is coming in, and investors will want the trading risk they are funding to be separable from the assets securing the debt.'
        : 'A sale or a further raise is intended, and separating assets from trading gives a buyer the option of taking one without the other.'
    );
    return { archetype: ARCHETYPES.spv, reasons, alsoConsider: null };
  }

  if (outsideCapital || a.exit === 'sell' || a.exit === 'raise') {
    if (outsideCapital) {
      reasons.push(
        a.capital === 'institutional'
          ? 'A DFI or fund will subscribe for shares with governance rights attached. Doing that at a holding company keeps the operating entity’s licences and customer contracts clear of the change-of-control questions a share issue otherwise raises.'
          : 'Foreign investors subscribing directly into the licensed operating entity can trigger change-of-control provisions in licences and customer contracts. A holding company is where that transaction belongs.'
      );
    }
    if (a.exit === 'sell') {
      reasons.push(
        'A trade sale is intended. Selling shares in a holding company is a materially simpler transaction than selling a business out of a trading entity, and it prices better.'
      );
    }
    if (a.exit === 'raise') {
      reasons.push('A later institutional round is intended, and that round will want to subscribe above the trading entity.');
    }
    return { archetype: ARCHETYPES.holdco, reasons, alsoConsider: heavyAssets ? ARCHETYPES.spv : null };
  }

  reasons.push(
    'One market, no outside capital, and no transaction in view. Nothing in the answers creates a problem that a second entity would solve.'
  );
  reasons.push(
    'This is a real recommendation, not a placeholder. Structures are easy to add when a reason appears and expensive to carry before one does.'
  );
  return { archetype: ARCHETYPES.single, reasons, alsoConsider: null };
}

/**
 * Questions for counsel, generated from the specific answers.
 *
 * This is where the rates that are deliberately absent from this module
 * get priced — by someone who can be professionally responsible for the
 * answer. Each item exists because of a particular answer, so the list
 * is short and every line earns its place.
 */
export function counselAgenda(a, selection) {
  const items = [];

  if (a.intercompany === 'yes') {
    items.push({
      trigger: 'Entities would charge each other',
      ask: 'What transfer pricing documentation is required in each market we operate in, and at what turnover does the obligation start? We need the intercompany agreements drafted before the charges begin, not after.'
    });
  }

  if (selection.archetype.id === 'regional' || selection.archetype.id === 'holdco') {
    items.push({
      trigger: 'A holding company is proposed',
      ask: 'Where should the holding company be incorporated, and what does that jurisdiction require of us to treat it as genuinely resident there — board composition, meeting location, local decision-making, records? Please state what we would have to do differently, not only what is permitted.'
    });
    items.push({
      trigger: 'Profits must reach shareholders',
      ask: 'What is the current withholding position on dividends from each operating country to the proposed holding company, and does any treaty apply on our facts? Please confirm the position as at today’s date and tell us when it was last changed.'
    });
  }

  if (selection.archetype.id === 'regional') {
    items.push({
      trigger: 'Entities in several markets',
      ask: 'Which of our operating markets impose local shareholding or local director requirements on a licensed freight or logistics operator, and are they satisfied by a regional holding company or only by resident individuals?'
    });
  }

  if (selection.archetype.id === 'spv' || selection.alsoConsider?.id === 'spv') {
    items.push({
      trigger: 'Assets held in a separate entity',
      ask: 'What lease rate between our asset company and our operating company would be defensible on arm’s-length terms, and what evidence should we keep to support it?'
    });
  }

  if (a.assets === 'property') {
    items.push({
      trigger: 'The business owns land or buildings',
      ask: 'What are the consequences of holding land through a company rather than directly, including on any future transfer of shares in that company? Some jurisdictions treat a share sale in a land-rich company as a land transfer.'
    });
  }

  if (a.capital === 'institutional') {
    items.push({
      trigger: 'A DFI or fund is investing',
      ask: 'What will this class of investor require in the constitutional documents — reserved matters, information rights, exit provisions — and does our proposed jurisdiction support them without a shareholders’ agreement governed elsewhere?'
    });
  }

  if (a.capital === 'foreign' || a.capital === 'institutional') {
    items.push({
      trigger: 'Investment from outside the operating country',
      ask: 'Are there foreign investment approvals, minimum capital requirements or exchange control notifications that apply to this investment, and what is the lead time on each?'
    });
  }

  if (a.exit === 'sell' || a.exit === 'raise') {
    items.push({
      trigger: 'A transaction is intended',
      ask: 'If we put this structure in place now and sell in five years, what would the disposal be taxed as, and would it have been different had we structured otherwise today? We would rather know the cost of this choice now than discover it at completion.'
    });
  }

  return items;
}
